import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Search, X, ArrowLeft, Package } from 'lucide-react';
import { Input } from '../components/ui/input';
import { useAuth } from '../context/AuthContext';
import { getLocalizedText } from '../components/MultiLanguageInput';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const SearchPage = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [searchParams] = useSearchParams();
  const initialQuery = searchParams.get('q') || '';
  const currentLang = i18n.language || 'en';
  
  const [search, setSearch] = useState(initialQuery);
  const [pools, setPools] = useState([]);
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    if (search.length >= 3) {
      searchPools();
    } else {
      setPools([]);
    }
  }, [search]);

  const searchPools = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/pools`);
      // Filter pools by search query - handle multi-language text
      const filtered = res.data.filter(pool => {
        const poolName = getLocalizedText(pool.name, currentLang);
        const poolDesc = getLocalizedText(pool.description, currentLang);
        return poolName.toLowerCase().includes(search.toLowerCase()) ||
          poolDesc?.toLowerCase().includes(search.toLowerCase());
      });
      setPools(filtered);
    } catch (err) {
      console.error('Search failed:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setSearch('');
    navigate('/home');
  };

  const handleBack = () => {
    navigate('/home');
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearch(value);
    
    // If search is cleared, go back to home
    if (value.length === 0) {
      navigate('/home');
    }
  };

  return (
    <div className="min-h-screen bg-[#FFFDF7]">
      {/* Search Header */}
      <div className="sticky top-0 z-50 bg-white border-b border-zinc-800">
        <div className="px-4 py-3 flex items-center gap-3">
          {/* Back Button */}
          <button
            onClick={handleBack}
            className="w-10 h-10 rounded-full bg-zinc-800 flex items-center justify-center text-white hover:bg-zinc-700 transition-colors"
            data-testid="search-back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>

          {/* Search Input */}
          <div className="flex-1 relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
            <Input
              value={search}
              onChange={handleSearchChange}
              placeholder={t('search.placeholder')}
              className="h-11 pl-11 pr-11 text-sm bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500 rounded-full"
              autoFocus
              data-testid="search-input"
            />
            {search && (
              <button
                onClick={handleClear}
                className="absolute right-3 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-zinc-700 flex items-center justify-center text-zinc-400 hover:text-white transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>

        {/* Search Info */}
        {search.length > 0 && search.length < 3 && (
          <div className="px-4 pb-3">
            <p className="text-zinc-500 text-sm">{t('search.minChars')}</p>
          </div>
        )}
      </div>

      {/* Results */}
      <div className="px-4 py-4">
        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-24 bg-zinc-900 rounded-xl animate-pulse" />
            ))}
          </div>
        ) : search.length >= 3 ? (
          <>
            {/* Results Count */}
            <p className="text-zinc-400 text-sm mb-4">
              {t('search.resultsFor', { count: pools.length })} "<span className="text-white">{search}</span>"
            </p>

            {pools.length === 0 ? (
              <div className="text-center py-16">
                <Package className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
                <p className="text-zinc-500">{t('search.noResults')}</p>
                <p className="text-zinc-600 text-sm mt-2">{t('search.tryDifferent')}</p>
              </div>
            ) : (
              <div className="space-y-3">
                {pools.map((pool, index) => (
                  <motion.div
                    key={pool.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    onClick={() => navigate(`/pool/${pool.id}`)}
                    className="flex gap-4 p-3 bg-zinc-900/50 border border-zinc-800 rounded-xl cursor-pointer hover:bg-zinc-800/50 active:scale-[0.99] transition-all"
                    data-testid={`search-result-${pool.id}`}
                  >
                    {/* Pool Image */}
                    <div className="w-20 h-20 rounded-lg overflow-hidden flex-shrink-0 bg-zinc-800">
                      <img 
                        src={pool.image_url} 
                        alt={getLocalizedText(pool.name, currentLang)}
                        className="w-full h-full object-cover"
                      />
                    </div>

                    {/* Pool Info */}
                    <div className="flex-1 min-w-0 py-1">
                      <h3 className="text-white font-semibold text-sm truncate mb-1">{getLocalizedText(pool.name, currentLang)}</h3>
                      <p className="text-zinc-500 text-xs line-clamp-2 mb-2">{getLocalizedText(pool.description, currentLang)}</p>
                      <div className="flex items-center gap-3">
                        <span className={`text-xs font-medium ${pool.remaining_quantity > 0 ? 'text-green-400' : 'text-amber-400'}`}>
                          {pool.remaining_quantity || 0} / {pool.total_quantity || 0} {t('search.remaining')}
                        </span>
                        <span className="text-yellow-400 text-xs font-medium">
                          {pool.price} {t('wallet.coins')}
                        </span>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-16">
            <Search className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
            <p className="text-zinc-500">{t('search.startTyping')}</p>
            <p className="text-zinc-600 text-sm mt-2">{t('search.searchTip')}</p>
          </div>
        )}
      </div>
    </div>
  );
};
